import type { InventoryProposal, ProposalComponent } from '../api/types';

export type ProposalRow = {
  index: number;
  component: ProposalComponent;
  checkable: boolean;
  action: string;
  raison: string | null;
};

function exemplaires(count: number): string {
  return count > 1 ? `${count} exemplaires` : '1 exemplaire';
}

function createAndMount(count: number): string {
  return count > 1 ? `créer ${count} exemplaires et les monter` : 'créer 1 exemplaire et le monter';
}

export function proposalRows(proposal: InventoryProposal): ProposalRow[] {
  const lines = new Map(proposal.lines.map((line) => [line.component, line]));
  const references = new Map(proposal.references.map((reference) => [reference.id, reference]));

  return proposal.components.map((component, index): ProposalRow => {
    const line = lines.get(index);
    if (line === undefined) {
      return {
        index,
        component,
        checkable: false,
        action: 'Rien à faire : déjà monté sur le poste à la date du diagnostic.',
        raison: null,
      };
    }

    if (line.action === 'nonApplicable') {
      return { index, component, checkable: false, action: 'Non applicable', raison: line.raison };
    }

    if (line.action === 'creationPuisMontage') {
      const reference = proposal.newReferences[line.newReference];
      const designation = reference?.designation ?? component.designation;
      return {
        index,
        component,
        checkable: true,
        action: `Nouvelle référence « ${designation} », suivie à l’exemplaire : ${createAndMount(line.exemplairesACreer)}.`,
        raison: null,
      };
    }

    const designation = references.get(line.referenceId)?.designation ?? component.designation;
    const steps: string[] = [];
    if (line.exemplairesDuStock.length > 0) {
      steps.push(`monter ${exemplaires(line.exemplairesDuStock.length)} pris au stock`);
    }
    if (line.exemplairesACreer > 0) steps.push(createAndMount(line.exemplairesACreer));
    return {
      index,
      component,
      checkable: true,
      action: `Référence existante « ${designation} » : ${steps.join(', puis ')}.`,
      raison: null,
    };
  });
}

export function retainedLines(rows: readonly ProposalRow[], checked: ReadonlySet<number>): number[] {
  return rows.filter((row) => row.checkable && checked.has(row.index)).map((row) => row.index);
}
